import React, { useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { useTheme } from '../../context/ThemeContext';

/**
 * Extracts style attributes from HTML elements containing video URLs
 * @param {string} content - HTML content
 * @param {string} url - Video URL to find context for
 * @returns {string} - Extracted style string or empty string
 */
const extractVideoStyles = (content, url) => {
  if (!content || !url) return '';

  // Look for the URL within styled elements
  const stylePatterns = [
    // Match div/p with style containing the URL
    new RegExp(`<(div|p)[^>]*style="([^"]*)"[^>]*>[^<]*${url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i'),
    // Match URL wrapped in styled anchor tag
    new RegExp(`<a[^>]*style="([^"]*)"[^>]*href="[^"]*${url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i'),
    // Match styled elements containing the URL
    new RegExp(`<[^>]*style="([^"]*)"[^>]*>[^<]*${url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i')
  ];

  for (const pattern of stylePatterns) {
    const match = content.match(pattern);
    if (match && (match[1] || match[2])) {
      return match[1] || match[2];
    }
  }

  return '';
};

/**
 * Merges database styles with default video container styles
 * @param {string} dbStyles - Styles from database content
 * @param {string} defaultStyles - Default video container styles
 * @returns {string} - Merged style string
 */
const mergeVideoStyles = (dbStyles, defaultStyles) => {
  if (!dbStyles) return defaultStyles;

  // Parse existing styles to avoid conflicts
  const dbStyleObj = {};
  const defaultStyleObj = {};

  // Parse database styles
  dbStyles.split(';').forEach(style => {
    const [key, value] = style.split(':').map(s => s.trim());
    if (key && value) {
      dbStyleObj[key] = value;
    }
  });

  // Parse default styles
  defaultStyles.split(';').forEach(style => {
    const [key, value] = style.split(':').map(s => s.trim());
    if (key && value) {
      defaultStyleObj[key] = value;
    }
  });

  // Merge (database styles take precedence for non-critical properties)
  const merged = { ...defaultStyleObj, ...dbStyleObj };

  // Ensure critical video properties are preserved
  merged['position'] = defaultStyleObj['position'] || 'relative';
  merged['width'] = defaultStyleObj['width'] || '100%';
  merged['height'] = defaultStyleObj['height'] || '0';
  merged['padding-bottom'] = defaultStyleObj['padding-bottom'] || '56.25%';

  return Object.entries(merged)
    .map(([key, value]) => `${key}: ${value}`)
    .join('; ');
};

// Video platform detection and conversion utilities
const VIDEO_PLATFORMS = {
  YOUTUBE: {
    patterns: [
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/
    ],
    getEmbedUrl: (videoId) => `https://www.youtube.com/embed/${videoId}`,
    getEmbedHtml: (videoId, dbStyles = '') => {
      const defaultStyles = 'position: relative; width: 100%; height: 0; padding-bottom: 56.25%; margin: 1rem 0';
      const mergedStyles = mergeVideoStyles(dbStyles, defaultStyles);
      return `
        <div style="${mergedStyles}">
          <iframe
            src="https://www.youtube.com/embed/${videoId}"
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
            frameborder="0"
            allowfullscreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          ></iframe>
        </div>
      `;
    }
  },
  VIMEO: {
    patterns: [
      /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/,
      /(?:https?:\/\/)?(?:player\.)?vimeo\.com\/video\/(\d+)/
    ],
    getEmbedUrl: (videoId) => `https://player.vimeo.com/video/${videoId}`,
    getEmbedHtml: (videoId, dbStyles = '') => {
      const defaultStyles = 'position: relative; width: 100%; height: 0; padding-bottom: 56.25%; margin: 1rem 0';
      const mergedStyles = mergeVideoStyles(dbStyles, defaultStyles);
      return `
        <div style="${mergedStyles}">
          <iframe
            src="https://player.vimeo.com/video/${videoId}"
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
            frameborder="0"
            allowfullscreen
            allow="autoplay; fullscreen; picture-in-picture"
          ></iframe>
        </div>
      `;
    }
  },
  DIRECT_VIDEO: {
    patterns: [
      /https?:\/\/[^\s]+\.(?:mp4|webm|ogg|mov|avi|wmv|flv|m4v)(?:\?[^\s]*)?$/i
    ],
    getEmbedHtml: (url, dbStyles = '') => {
      const defaultContainerStyles = 'margin: 1rem 0; width: 100%; max-width: 100%';
      const defaultVideoStyles = 'width: 100%; max-width: 100%; height: auto; display: block';
      const mergedContainerStyles = mergeVideoStyles(dbStyles, defaultContainerStyles);
      return `
        <div style="${mergedContainerStyles}">
          <video
            controls
            style="${defaultVideoStyles}"
            preload="metadata"
            playsinline
          >
            <source src="${url}" type="video/${url.split('.').pop().split('?')[0]}">
            Your browser does not support the video tag.
            <a href="${url}" target="_blank" rel="noopener noreferrer" style="display: inline-block; margin-top: 0.5rem; color: #1976d2; text-decoration: none;">Download video</a>
          </video>
        </div>
      `;
    }
  },
  DAILYMOTION: {
    patterns: [
      /(?:https?:\/\/)?(?:www\.)?dailymotion\.com\/video\/([a-zA-Z0-9]+)/
    ],
    getEmbedHtml: (videoId, dbStyles = '') => {
      const defaultStyles = 'position: relative; width: 100%; height: 0; padding-bottom: 56.25%; margin: 1rem 0';
      const mergedStyles = mergeVideoStyles(dbStyles, defaultStyles);
      return `
        <div style="${mergedStyles}">
          <iframe
            src="https://www.dailymotion.com/embed/video/${videoId}"
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
            frameborder="0"
            allowfullscreen
            allow="autoplay; fullscreen"
          ></iframe>
        </div>
      `;
    }
  },
  TWITCH: {
    patterns: [
      /(?:https?:\/\/)?(?:www\.)?twitch\.tv\/videos\/(\d+)/,
      /(?:https?:\/\/)?(?:clips\.)?twitch\.tv\/([a-zA-Z0-9_-]+)(?:\/clip\/([a-zA-Z0-9_-]+))?/
    ],
    getEmbedHtml: (videoId, clipId, dbStyles = '') => {
      const embedUrl = clipId
        ? `https://clips.twitch.tv/embed?clip=${clipId}&parent=${window.location.hostname}`
        : `https://player.twitch.tv/?video=${videoId}&parent=${window.location.hostname}`;

      const defaultStyles = 'position: relative; width: 100%; height: 0; padding-bottom: 56.25%; margin: 1rem 0';
      const mergedStyles = mergeVideoStyles(dbStyles, defaultStyles);

      return `
        <div style="${mergedStyles}">
          <iframe
            src="${embedUrl}"
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
            frameborder="0"
            allowfullscreen
            allow="autoplay; fullscreen"
          ></iframe>
        </div>
      `;
    }
  },
  GENERIC_FALLBACK: {
    patterns: [
      /https?:\/\/[^\s]*(?:video|watch|embed|play)[^\s]*/i
    ],
    getEmbedHtml: (url, dbStyles = '') => {
      const defaultStyles = 'margin: 1rem 0; padding: 1rem; border: 1px solid #e1e5e9; border-radius: 8px; background-color: #f8f9fa';
      const mergedStyles = mergeVideoStyles(dbStyles, defaultStyles);
      return `
        <div style="${mergedStyles}">
          <p style="margin: 0 0 0.5rem 0; font-weight: 600;">Video Link:</p>
          <a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #1976d2; text-decoration: none;">
            ${url.length > 60 ? url.substring(0, 60) + '...' : url}
          </a>
          <p style="margin: 0.5rem 0 0 0; font-size: 0.875rem; color: #666;">
            Click to open video in new tab
          </p>
        </div>
      `;
    }
  }
};

/**
 * Detects video URLs and converts them to appropriate embed code
 * @param {string} content - HTML content that may contain video links
 * @returns {string} - Content with video links converted to embeds
 */
const convertVideoLinksToEmbeds = (content) => {
  if (!content) return content;

  let processedContent = content;
  const processedUrls = new Set(); // Avoid processing the same URL multiple times

  // Process each video platform (order matters - more specific patterns first)
  const platformOrder = ['YOUTUBE', 'VIMEO', 'DAILYMOTION', 'TWITCH', 'DIRECT_VIDEO', 'GENERIC_FALLBACK'];

  platformOrder.forEach(platform => {
    const config = VIDEO_PLATFORMS[platform];
    if (!config) return;

    config.patterns.forEach(pattern => {
      // Handle URLs wrapped in anchor tags
      const anchorRegex = new RegExp(`<a[^>]*href=['"]([^'"]*?)['"][^>]*>([^<]*)</a>`, 'gi');
      processedContent = processedContent.replace(anchorRegex, (match, url, linkText) => {
        if (processedUrls.has(url)) return match;

        const videoMatch = url.match(pattern);
        if (videoMatch) {
          processedUrls.add(url);
          const dbStyles = extractVideoStyles(processedContent, url);

          if (platform === 'DIRECT_VIDEO' || platform === 'GENERIC_FALLBACK') {
            return config.getEmbedHtml(url, dbStyles);
          } else if (platform === 'TWITCH') {
            return config.getEmbedHtml(videoMatch[1], videoMatch[2], dbStyles);
          } else {
            return config.getEmbedHtml(videoMatch[1], dbStyles);
          }
        }
        return match;
      });

      // Handle plain URLs (not wrapped in anchor tags)
      processedContent = processedContent.replace(pattern, (match, ...groups) => {
        if (processedUrls.has(match)) return match;

        // Skip if this URL is already inside an HTML tag
        const beforeMatch = processedContent.substring(0, processedContent.indexOf(match));
        const afterMatch = processedContent.substring(processedContent.indexOf(match) + match.length);
        if (beforeMatch.lastIndexOf('<') > beforeMatch.lastIndexOf('>') ||
            afterMatch.indexOf('>') < afterMatch.indexOf('<')) {
          return match;
        }

        processedUrls.add(match);
        const dbStyles = extractVideoStyles(processedContent, match);

        if (platform === 'DIRECT_VIDEO' || platform === 'GENERIC_FALLBACK') {
          return config.getEmbedHtml(match, dbStyles);
        } else if (platform === 'TWITCH') {
          return config.getEmbedHtml(groups[0], groups[1], dbStyles);
        } else {
          return config.getEmbedHtml(groups[0], dbStyles);
        }
      });
    });
  });

  return processedContent;
};

const RichTextRenderer = ({ 
  content, 
  className = '', 
  style = {},
  fontSize = '16px',
  lineHeight = '1.6',
  maxWidth = 'none'
}) => {
  const containerRef = useRef(null);
  const shadowRootRef = useRef(null);
  const { isDark } = useTheme();

  useEffect(() => {
    if (!containerRef.current) return;

    // Create Shadow DOM if not exists
    if (!shadowRootRef.current) {
      shadowRootRef.current = containerRef.current.attachShadow({ mode: 'open' });
    }

    const shadowRoot = shadowRootRef.current;

    // Ensure the dark class is in sync before reading computed styles.
    // This avoids a race where isDark changes but the ThemeContext effect
    // hasn't toggled the class yet.
    const root = document.documentElement;
    if (isDark && !root.classList.contains('dark')) root.classList.add('dark');
    if (!isDark && root.classList.contains('dark')) root.classList.remove('dark');

    // Resolve theme tokens from the document root so shadow DOM inherits them
    const rootStyles = getComputedStyle(document.documentElement);
    const primaryHSL = (rootStyles.getPropertyValue('--primary') || '174 72% 40%').trim();
    const foregroundHSL = (rootStyles.getPropertyValue('--foreground') || '220 20% 10%').trim();
    const mutedFgHSL = (rootStyles.getPropertyValue('--muted-foreground') || '215 16% 47%').trim();
    const borderHSL = (rootStyles.getPropertyValue('--border') || '180 12% 90%').trim();

    // Whether to apply the lesson editorial treatment
    const isLesson = (className || '').includes('lesson-content');
    const baseFontSize = fontSize || '17px';
    const baseLineHeight = lineHeight || '1.7';
    const baseMaxWidth = maxWidth && maxWidth !== 'none' ? maxWidth : 'none';

    // Responsive styles for all media elements
    const styles = `
      <style>
        :host {
          display: block;
          --p: ${primaryHSL};
          --fg: ${foregroundHSL};
          --mfg: ${mutedFgHSL};
          --bd: ${borderHSL};
        }

        .rich-content {
          display: block;
          width: 100%;
          overflow-wrap: break-word;
          word-wrap: break-word;
          font-family: "Plus Jakarta Sans", system-ui, -apple-system, sans-serif;
          font-size: ${baseFontSize};
          line-height: ${baseLineHeight};
          color: hsl(var(--fg));
          ${baseMaxWidth !== 'none' ? `max-width: ${baseMaxWidth};` : ''}
        }

        ${isLesson ? `
        /* Editorial measure: text wraps at ~64ch, LEFT-anchored so it aligns
           with the chapter heading. Media breaches to full article width.

           :not(:has(img/iframe/video)) — a block holding media is not a text block, so it
           must not be capped. Images sitting bare in a <p> get hoisted out below and reach
           full width, but a linked image (<p><a><img></a></p>) can't be hoisted without
           breaking the link, so it stays put and would otherwise be stuck at 64ch. That
           mismatch is what made some lesson images render smaller than others. */
        .rich-content > p:not([style]):not(:has(img)):not(:has(iframe)):not(:has(video)),
        .rich-content > h1:not([style]):not(:has(img)),
        .rich-content > h2:not([style]):not(:has(img)),
        .rich-content > h3:not([style]):not(:has(img)),
        .rich-content > h4:not([style]):not(:has(img)),
        .rich-content > h5:not([style]):not(:has(img)),
        .rich-content > h6:not([style]):not(:has(img)),
        .rich-content > ul:not([style]),
        .rich-content > ol:not([style]),
        .rich-content > blockquote:not([style]) {
          max-width: 64ch;
          margin-left: 0;
          margin-right: auto;
        }
        ` : ''}

        /* Force all lesson text to use theme-aware foreground color,
           overriding any inline color styles from the rich text editor */
        ${isLesson ? `
        .rich-content *:not(a):not(.ql-action-btn-wrapper):not(.ql-action-btn-wrapper *) {
          color: hsl(var(--fg)) !important;
        }
        .rich-content a:not([style]):not(.ql-action-btn-wrapper a) {
          color: hsl(var(--fg)) !important;
        }
        ` : ''}

        strong, b { font-weight: 700; }
        em, i { font-style: italic; }

        ${isLesson ? `
        /* Editorial paragraph rhythm */
        .rich-content p:not([style]) {
          margin: 0 0 1.1em 0;
          color: hsl(var(--fg) / 0.88);
        }
        .rich-content > p:not([style]):first-of-type {
          margin-top: 0;
        }
        .rich-content > p:not([style]):last-child {
          margin-bottom: 0;
        }

        /* Drop accent on first paragraph: a hairline indent rule */
        .rich-content > p:not([style]):first-of-type::before {
          content: "";
          display: block;
          width: 36px;
          height: 2px;
          background: hsl(var(--p));
          margin-bottom: 1.1em;
        }

        /* Headings — editorial hierarchy, display-weight, tight */
        .rich-content h1:not([style]),
        .rich-content h2:not([style]) {
          font-size: 1.6em;
          font-weight: 700;
          letter-spacing: -0.02em;
          line-height: 1.2;
          color: hsl(var(--fg));
          margin: 2em 0 0.6em 0;
        }
        .rich-content h3:not([style]) {
          font-size: 1.25em;
          font-weight: 700;
          letter-spacing: -0.015em;
          line-height: 1.25;
          color: hsl(var(--fg));
          margin: 1.8em 0 0.5em 0;
        }
        .rich-content h4:not([style]),
        .rich-content h5:not([style]),
        .rich-content h6:not([style]) {
          font-size: 0.78em;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: hsl(var(--p));
          margin: 1.6em 0 0.4em 0;
        }

        /* Blockquote — pulled quote with a primary mark */
        .rich-content blockquote:not([style]) {
          margin: 1.6em 0;
          padding: 0.2em 0 0.2em 1.25em;
          border-left: 2px solid hsl(var(--p));
          font-style: italic;
          font-size: 1.08em;
          line-height: 1.55;
          color: hsl(var(--fg) / 0.85);
        }

        /* Lists */
        .rich-content ul:not([style]),
        .rich-content ol:not([style]) {
          margin: 0 0 1.1em 0;
          padding-left: 1.4em;
        }
        .rich-content li:not([style]) {
          margin-bottom: 0.4em;
          color: hsl(var(--fg) / 0.88);
        }
        .rich-content ul:not([style]) li::marker {
          color: hsl(var(--p));
        }
        .rich-content ol:not([style]) li::marker {
          color: hsl(var(--p));
          font-weight: 700;
          font-variant-numeric: tabular-nums;
        }

        /* Action button links (inserted via custom Quill blot) */
        .rich-content .ql-action-btn-wrapper {
          margin: 4px 0 !important;
        }
        .rich-content .ql-action-btn-wrapper a {
          display: inline-block !important;
          padding: 12px 28px !important;
          background-color: hsl(var(--p)) !important;
          color: #ffffff !important;
          text-decoration: none !important;
          border-radius: 8px !important;
          font-weight: 600 !important;
          font-size: 14px !important;
          text-align: center !important;
          cursor: pointer !important;
          transition: opacity 0.2s ease, transform 0.2s ease !important;
          background-image: none !important;
          box-shadow: 0 2px 8px hsl(var(--p) / 0.3) !important;
        }
        .rich-content .ql-action-btn-wrapper a:hover {
          opacity: 0.9 !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 12px hsl(var(--p) / 0.4) !important;
        }

        /* Links */
        .rich-content a:not([style]) {
          color: hsl(var(--fg));
          text-decoration: none;
          background-image: linear-gradient(hsl(var(--p)), hsl(var(--p)));
          background-size: 100% 1px;
          background-repeat: no-repeat;
          background-position: 0 100%;
          padding-bottom: 1px;
          transition: color 0.15s ease;
        }
        .rich-content a:not([style]):hover {
          color: hsl(var(--p));
        }

        /* Inline code */
        .rich-content code:not([style]) {
          font-family: ui-monospace, "SF Mono", Menlo, monospace;
          font-size: 0.9em;
          padding: 0.1em 0.35em;
          background: hsl(var(--p) / 0.08);
          border-radius: 2px;
          color: hsl(var(--fg));
        }
        .rich-content pre:not([style]) {
          font-family: ui-monospace, "SF Mono", Menlo, monospace;
          font-size: 0.88em;
          padding: 1em 1.2em;
          background: hsl(var(--fg) / 0.04);
          border-left: 2px solid hsl(var(--p));
          overflow-x: auto;
          margin: 1.4em 0;
        }
        .rich-content pre code { background: transparent; padding: 0; }

        /* Horizontal rule — a deliberate section break, left-anchored */
        .rich-content hr:not([style]) {
          border: 0;
          height: 1px;
          background: hsl(var(--bd));
          margin: 2.2em 0;
          width: 30%;
          position: relative;
        }

        /* Embedded video frame — breaches the text measure on purpose,
           full-bleed in the article. !important overrides any DB inline width. */
        .rich-content iframe {
          border-radius: 3px;
          background: hsl(var(--fg) / 0.04);
        }
        .rich-content div:has(> iframe) {
          margin: 2em auto !important;
          width: 100% !important;
          max-width: 100% !important;
          box-shadow: 0 1px 0 hsl(var(--bd)), 0 24px 60px -28px hsl(var(--fg) / 0.35);
          border-radius: 4px;
          overflow: hidden;
        }
        /* Some lesson HTML wraps the video in a paragraph — bust out of that too */
        .rich-content p:has(> iframe),
        .rich-content p:has(> div > iframe) {
          max-width: 100% !important;
          margin: 2em auto !important;
        }

        /* Images — breach the measure */
        .rich-content img:not([style]) {
          display: block;
          margin: 1.8em 0;
          max-width: 100%;
          border-radius: 3px;
        }

        /* Tables */
        .rich-content table:not([style]) {
          font-size: 0.95em;
          margin: 1.4em 0;
        }
        .rich-content th:not([style]) {
          text-align: left;
          font-weight: 700;
          font-size: 0.78em;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: hsl(var(--mfg));
          padding: 0.65em 0.8em;
          border-bottom: 1px solid hsl(var(--bd));
        }
        .rich-content td:not([style]) {
          padding: 0.65em 0.8em;
          border-bottom: 1px solid hsl(var(--bd));
          vertical-align: top;
        }
        ` : `
        /* Non-lesson use (e.g. editor preview): keep headings unstyled per legacy behavior */
        h1:not([style]), h2:not([style]), h3:not([style]),
        h4:not([style]), h5:not([style]), h6:not([style]) {
          font-weight: normal;
        }
        `}

        /* Responsive Images */
        .rich-content img {
          max-width: 100%;
          height: auto;
          display: block;
          margin: 0.5rem 0;
        }

        /* Responsive Videos (HTML5) */
        .rich-content video {
          max-width: 100%;
          height: auto;
          display: block;
        }

        /* Responsive Tables */
        .rich-content table {
          width: 100%;
          border-collapse: collapse;
          overflow-x: auto;
          display: block;
          white-space: nowrap;
        }

        /* Table wrapper for horizontal scroll on mobile */
        .rich-content table {
          display: table;
          width: 100%;
          max-width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        /* Responsive table container */
        .table-responsive {
          width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        /* Mobile optimizations */
        @media (max-width: 768px) {
          .rich-content {
            font-size: 14px;
            line-height: 1.5;
          }

          /* Smaller margins on mobile */
          .rich-content > * {
            margin-top: 0.75rem;
            margin-bottom: 0.75rem;
          }

          /* Responsive video containers */
          .rich-content iframe,
          .rich-content video {
            max-width: 100%;
            height: auto;
          }

          /* Mobile image enhancements */
          .rich-content img {
            max-width: 100%;
            width: auto !important;
            height: auto !important;
            margin: 0.75rem 0;
            cursor: pointer;
            transition: transform 0.2s ease;
          }

          .rich-content img:hover {
            transform: scale(1.02);
          }

          .rich-content img.zoomed {
            transform: scale(1.5);
            z-index: 10;
            position: relative;
          }

          /* Table responsive behavior */
          .rich-content table {
            font-size: 12px;
            display: block;
            overflow-x: auto;
            white-space: nowrap;
            width: 100%;
          }

          .rich-content th,
          .rich-content td {
            padding: 0.5rem 0.25rem;
            white-space: nowrap;
          }
        }

        /* Extra small screens */
        @media (max-width: 480px) {
          .rich-content {
            font-size: 13px;
          }

          .rich-content table {
            font-size: 11px;
          }

          .rich-content th,
          .rich-content td {
            padding: 0.25rem 0.125rem;
          }
        }
      </style>
    `;

    // Convert video links to embeds before sanitization
    const contentWithVideoEmbeds = convertVideoLinksToEmbeds(content || '<p>No content available</p>');

    // Sanitize content and create DOM structure
    const sanitizedContent = DOMPurify.sanitize(contentWithVideoEmbeds, {
      ALLOWED_TAGS: [
        // Text elements
        'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'mark', 'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li', 'code', 'pre', 'blockquote', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'img', 'hr', 'div', 'span', 'sub', 'sup',
        // Video elements
        'iframe', 'video', 'source'
      ],
      ALLOWED_ATTR: [
        // Standard attributes
        'href', 'target', 'rel', 'title', 'alt', 'src', 'width', 'height', 'class', 'id', 'style',
        // Video-specific attributes
        'controls', 'autoplay', 'muted', 'loop', 'poster', 'preload', 'type',
        // Iframe-specific attributes
        'frameborder', 'allowfullscreen', 'allow', 'loading', 'sandbox'
      ],
      ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
      // Allow YouTube and Vimeo domains for iframe embeds
      ALLOWED_SCHEMES: ['http', 'https', 'data'],
      ADD_TAGS: ['iframe'],
      ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling']
    });

    shadowRoot.innerHTML = `${styles}<div class="rich-content">${sanitizedContent}</div>`;

    // Ensure all inline styles are properly applied in Shadow DOM
    const styledElements = shadowRoot.querySelectorAll('[style]');
    styledElements.forEach(element => {
      const styleAttr = element.getAttribute('style');
      if (styleAttr) {
        element.style.cssText = styleAttr;
      }
    });

    // Post-process for responsive media elements
    const processResponsiveMedia = () => {
      // Force every image into a full-bleed responsive frame (lesson context only).
      // Same pattern as iframes: strip stored width/height attrs and inline width styles
      // that were keeping images tiny, then set our own with !important.
      const images = shadowRoot.querySelectorAll('img');
      images.forEach(img => {
        if (isLesson) {
          // An author-set size wins; without one the image goes full-bleed as before.
          // Read the stored width before clearing anything. A bare `width="480"` attribute
          // is px; a style may carry any unit, so it is passed through untouched.
          const widthAttr = img.getAttribute('width');
          const authorWidth =
            img.style.width ||
            (widthAttr ? (/^\d+$/.test(widthAttr) ? `${widthAttr}px` : widthAttr) : '');

          img.removeAttribute('width');
          img.removeAttribute('height');

          // Strip the rest so our !important wins
          img.style.removeProperty('width');
          img.style.removeProperty('max-width');
          img.style.removeProperty('height');
          img.style.removeProperty('min-width');
          img.style.removeProperty('min-height');

          img.style.setProperty('display', 'block', 'important');
          img.style.setProperty('width', authorWidth || '100%', 'important');
          img.style.setProperty('max-width', '100%', 'important');
          img.style.setProperty('height', 'auto', 'important');
          img.style.setProperty('margin', '2em 0', 'important');
          img.style.setProperty('border-radius', '4px', 'important');
          img.style.setProperty('cursor', 'zoom-in', 'important');

          // If the image is inside a <p>, hoist it out — same reason we hoist iframes.
          const parent = img.parentElement;
          if (parent && parent.tagName === 'P') {
            parent.parentElement.insertBefore(img, parent.nextSibling);
            if (!parent.textContent.trim() && !parent.querySelector('img,iframe,video')) {
              parent.remove();
            }
          }
        } else {
          // Non-lesson contexts (editor preview, etc.) — keep existing light-touch behavior
          const currentStyle = img.getAttribute('style') || '';
          if (!currentStyle.includes('max-width') && !currentStyle.includes('width')) {
            img.style.maxWidth = '100%';
            img.style.height = 'auto';
            img.style.display = 'block';
          }
        }

        if (img.hasAttribute('width')) {
          img.setAttribute('data-original-width', img.getAttribute('width'));
        }
        if (img.hasAttribute('height')) {
          img.setAttribute('data-original-height', img.getAttribute('height'));
        }

        // Mobile responsive fallback for non-lesson contexts
        const applyResponsiveStyles = () => {
          const isMobile = window.innerWidth <= 640;
          if (isMobile && !isLesson) {
            img.removeAttribute('width');
            img.removeAttribute('height');
            img.style.maxWidth = '100%';
            img.style.width = 'auto';
            img.style.height = 'auto';
          }
        };
        applyResponsiveStyles();
        window.addEventListener('resize', applyResponsiveStyles);

        // Add touch-friendly zoom on mobile
        img.addEventListener('click', (e) => {
          const isMobile = window.innerWidth <= 640;
          if (isMobile) {
            e.preventDefault();
            img.classList.toggle('zoomed');

            // Remove zoom after 3 seconds
            if (img.classList.contains('zoomed')) {
              setTimeout(() => {
                img.classList.remove('zoomed');
              }, 3000);
            }
          }
        });
      });

      // Wrap tables for responsive horizontal scrolling
      const tables = shadowRoot.querySelectorAll('table');
      tables.forEach(table => {
        if (!table.parentElement.classList.contains('table-responsive')) {
          const wrapper = document.createElement('div');
          wrapper.className = 'table-responsive';
          wrapper.style.cssText = 'width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; margin: 1rem 0;';
          table.parentNode.insertBefore(wrapper, table);
          wrapper.appendChild(table);
        }
      });

      // Force every iframe (DB-native or converted) into a full-width 16:9 wrapper.
      // Strips the stored width/height attrs that were keeping videos tiny.
      const iframes = shadowRoot.querySelectorAll('iframe');
      iframes.forEach(iframe => {
        iframe.removeAttribute('width');
        iframe.removeAttribute('height');

        const parent = iframe.parentElement;
        const parentIsResponsiveWrapper =
          parent && parent.tagName === 'DIV' &&
          (parent.style.paddingBottom || '').includes('56.25');

        let wrapper;
        if (parentIsResponsiveWrapper) {
          wrapper = parent;
        } else {
          // Build a fresh wrapper around the bare iframe
          wrapper = document.createElement('div');
          parent.insertBefore(wrapper, iframe);
          wrapper.appendChild(iframe);
        }

        // Force the wrapper to full responsive width — overrides any DB inline styles
        wrapper.style.setProperty('position', 'relative', 'important');
        wrapper.style.setProperty('width', '100%', 'important');
        wrapper.style.setProperty('max-width', '100%', 'important');
        wrapper.style.setProperty('height', '0', 'important');
        wrapper.style.setProperty('padding-bottom', '56.25%', 'important');
        wrapper.style.setProperty('margin', '2em auto', 'important');
        wrapper.style.setProperty('border-radius', '4px', 'important');
        wrapper.style.setProperty('overflow', 'hidden', 'important');
        wrapper.style.setProperty(
          'box-shadow',
          '0 1px 0 hsl(var(--border)), 0 24px 60px -28px hsl(var(--foreground) / 0.35)',
          'important'
        );

        // Force iframe to absolutely fill the wrapper
        iframe.style.setProperty('position', 'absolute', 'important');
        iframe.style.setProperty('top', '0', 'important');
        iframe.style.setProperty('left', '0', 'important');
        iframe.style.setProperty('width', '100%', 'important');
        iframe.style.setProperty('height', '100%', 'important');
        iframe.style.setProperty('border', '0', 'important');

        // If the wrapper ended up inside a <p>, hoist it out — block in inline is invalid
        // and most browsers either auto-close the <p> early or distort layout.
        const grandparent = wrapper.parentElement;
        if (grandparent && grandparent.tagName === 'P') {
          grandparent.parentElement.insertBefore(wrapper, grandparent.nextSibling);
          if (!grandparent.textContent.trim() && !grandparent.querySelector('img,iframe,video')) {
            grandparent.remove();
          }
        }
      });

      // Same treatment for <video> elements stored directly in the DB
      const videos = shadowRoot.querySelectorAll('video');
      videos.forEach(video => {
        video.removeAttribute('width');
        video.removeAttribute('height');
        video.style.setProperty('display', 'block', 'important');
        video.style.setProperty('width', '100%', 'important');
        video.style.setProperty('max-width', '100%', 'important');
        video.style.setProperty('height', 'auto', 'important');
        video.style.setProperty('margin', '2em auto', 'important');
        video.style.setProperty('border-radius', '4px', 'important');
        video.style.setProperty(
          'box-shadow',
          '0 1px 0 hsl(var(--border)), 0 24px 60px -28px hsl(var(--foreground) / 0.35)',
          'important'
        );
      });
    };

    processResponsiveMedia();

    // Handle link clicks within shadow DOM
    const links = shadowRoot.querySelectorAll('a');
    links.forEach(link => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        if (href && href.startsWith('http')) {
          e.preventDefault();
          window.open(href, '_blank', 'noopener,noreferrer');
        }
      });
    });

  }, [content, fontSize, lineHeight, maxWidth, className, isDark]);

  return (
    <div 
      ref={containerRef}
      className={`rich-text-renderer ${className}`}
      style={style}
      role="article"
      aria-label="Rich text content"
    />
  );
};

export default RichTextRenderer;